import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Plus, Trash2, Pencil, X, DollarSign, Users, Package, ShoppingBag, Loader2 } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import Nav from "@/components/Nav";

const EMPTY = { name: "", tagline: "", description: "", level: "Beginner", duration: "", price: "", interval: "month",
  image: "https://images.unsplash.com/photo-1617791160536-598cf32026fb", features: "", accent: "#00E5FF", published: true };

export default function Admin() {
  const [tab, setTab] = useState("programs");
  const [stats, setStats] = useState(null);
  const [programs, setPrograms] = useState([]);
  const [orders, setOrders] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);

  const load = () => {
    api.get("/admin/stats").then((r) => setStats(r.data)).catch(() => {});
    api.get("/programs/all").then((r) => setPrograms(r.data)).catch(() => {});
    api.get("/admin/orders").then((r) => setOrders(r.data)).catch(() => {});
  };
  useEffect(load, []);

  const openNew = () => { setEditing(null); setForm(EMPTY); setShowForm(true); };
  const openEdit = (p) => {
    setEditing(p.id);
    setForm({ ...p, price: String(p.price), features: (p.features || []).join("\n") });
    setShowForm(true);
  };

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    const payload = {
      ...form, price: parseFloat(form.price),
      features: form.features.split("\n").map((f) => f.trim()).filter(Boolean),
    };
    try {
      if (editing) await api.put(`/programs/${editing}`, payload);
      else await api.post("/programs", payload);
      toast.success(editing ? "Program updated" : "Program created & synced to Stripe");
      setShowForm(false); load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Save failed");
    } finally { setSaving(false); }
  };

  const del = async (id) => {
    if (!window.confirm("Delete this program?")) return;
    try { await api.delete(`/programs/${id}`); toast.success("Deleted"); load(); }
    catch { toast.error("Delete failed"); }
  };

  const statCards = stats ? [
    { icon: DollarSign, label: "Revenue", value: `$${stats.revenue}` },
    { icon: ShoppingBag, label: "Paid orders", value: stats.total_orders },
    { icon: Users, label: "Users", value: stats.total_users },
    { icon: Package, label: "Programs", value: stats.total_programs },
  ] : [];

  return (
    <div className="min-h-screen bg-soma-dashbg">
      <Nav />
      <div className="max-w-[1300px] mx-auto px-6 lg:px-10 pt-32 pb-24">
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div>
            <span className="text-xs uppercase tracking-[0.35em] text-soma-primary">Control room</span>
            <h1 className="font-display font-semibold text-4xl lg:text-5xl mt-3">Admin</h1>
          </div>
          <button onClick={openNew} data-testid="admin-new-program"
            className="flex items-center gap-2 px-6 py-3 rounded-full bg-soma-primary text-soma-bg font-semibold hover:bg-soma-primaryHover transition-colors">
            <Plus className="w-4 h-4" /> New program
          </button>
        </div>

        <div className="grid grid-cols-2 lg:grid-cols-4 gap-px bg-soma-border border border-soma-border mt-10">
          {statCards.map((s, i) => (
            <div key={i} className="bg-soma-dashbg p-6" data-testid={`admin-stat-${s.label.toLowerCase()}`}>
              <s.icon className="w-5 h-5 text-soma-primary mb-4" strokeWidth={1.5} />
              <div className="font-display font-semibold text-3xl">{s.value}</div>
              <div className="text-xs text-soma-muted mt-1">{s.label}</div>
            </div>
          ))}
        </div>

        <div className="flex gap-6 mt-14 border-b border-soma-border">
          {["programs", "orders"].map((t) => (
            <button key={t} onClick={() => setTab(t)} data-testid={`admin-tab-${t}`}
              className={`pb-4 text-sm uppercase tracking-[0.2em] transition-colors ${tab === t ? "text-white border-b-2 border-soma-primary" : "text-soma-muted"}`}>
              {t}
            </button>
          ))}
        </div>

        {tab === "programs" && (
          <div className="mt-8 border border-soma-border divide-y divide-soma-border" data-testid="admin-programs-list">
            {programs.map((p) => (
              <div key={p.id} className="bg-soma-surface p-5 flex items-center gap-4">
                <img src={p.image} alt="" className="w-14 h-14 object-cover border border-soma-border" />
                <div className="flex-1 min-w-0">
                  <div className="font-semibold text-white truncate">{p.name}</div>
                  <div className="text-sm text-soma-muted">${p.price}/{p.interval} · {p.level} {!p.published && "· hidden"}</div>
                </div>
                <button onClick={() => openEdit(p)} data-testid={`edit-${p.id}`} className="p-2 text-soma-muted hover:text-soma-primary transition-colors"><Pencil className="w-4 h-4" /></button>
                <button onClick={() => del(p.id)} data-testid={`delete-${p.id}`} className="p-2 text-soma-muted hover:text-red-400 transition-colors"><Trash2 className="w-4 h-4" /></button>
              </div>
            ))}
          </div>
        )}

        {tab === "orders" && (
          <div className="mt-8 border border-soma-border divide-y divide-soma-border" data-testid="admin-orders-list">
            {orders.length === 0 && <div className="bg-soma-surface p-8 text-soma-muted">No orders yet.</div>}
            {orders.map((o) => (
              <div key={o.id} className="bg-soma-surface p-5 flex items-center justify-between gap-4">
                <div>
                  <div className="font-semibold text-white">{o.program_name}</div>
                  <div className="text-sm text-soma-muted">{(o.order_id || o.session_id || "").slice(0, 24)}…</div>
                </div>
                <div className="text-right">
                  <div className="text-white">${o.amount}</div>
                  <div className={`text-xs ${o.payment_status === "paid" ? "text-soma-accent" : "text-soma-muted"}`}>{o.payment_status}</div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {showForm && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm" onClick={() => setShowForm(false)}>
          <motion.form initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} onClick={(e) => e.stopPropagation()} onSubmit={save}
            data-testid="program-form"
            className="w-full max-w-2xl max-h-[85vh] overflow-y-auto glass border border-white/10 p-8">
            <div className="flex items-center justify-between mb-8">
              <h2 className="font-display font-semibold text-2xl">{editing ? "Edit program" : "New program"}</h2>
              <button type="button" onClick={() => setShowForm(false)}><X className="w-5 h-5 text-soma-muted hover:text-white" /></button>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {[["name", "Name"], ["tagline", "Tagline"], ["level", "Level"], ["duration", "Duration"], ["image", "Image URL"], ["accent", "Accent hex"]].map(([k, label]) => (
                <div key={k}>
                  <label className="text-xs uppercase tracking-[0.15em] text-soma-muted">{label}</label>
                  <input data-testid={`form-${k}`} required={k !== "accent"} value={form[k]} onChange={(e) => setForm({ ...form, [k]: e.target.value })}
                    className="mt-1 w-full bg-soma-surface border border-soma-border px-3 py-2 text-white outline-none focus:border-soma-primary" />
                </div>
              ))}
              <div>
                <label className="text-xs uppercase tracking-[0.15em] text-soma-muted">Price (USD)</label>
                <input data-testid="form-price" type="number" step="0.01" required value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })}
                  className="mt-1 w-full bg-soma-surface border border-soma-border px-3 py-2 text-white outline-none focus:border-soma-primary" />
              </div>
              <div>
                <label className="text-xs uppercase tracking-[0.15em] text-soma-muted">Interval</label>
                <select data-testid="form-interval" value={form.interval} onChange={(e) => setForm({ ...form, interval: e.target.value })}
                  className="mt-1 w-full bg-soma-surface border border-soma-border px-3 py-2 text-white outline-none focus:border-soma-primary">
                  <option value="month">Monthly</option>
                  <option value="year">Yearly</option>
                  <option value="onetime">One-time</option>
                </select>
              </div>
            </div>
            <div className="mt-4">
              <label className="text-xs uppercase tracking-[0.15em] text-soma-muted">Description</label>
              <textarea data-testid="form-description" required rows={3} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })}
                className="mt-1 w-full bg-soma-surface border border-soma-border px-3 py-2 text-white outline-none focus:border-soma-primary" />
            </div>
            <div className="mt-4">
              <label className="text-xs uppercase tracking-[0.15em] text-soma-muted">Features (one per line)</label>
              <textarea data-testid="form-features" rows={4} value={form.features} onChange={(e) => setForm({ ...form, features: e.target.value })}
                className="mt-1 w-full bg-soma-surface border border-soma-border px-3 py-2 text-white outline-none focus:border-soma-primary" />
            </div>
            <label className="flex items-center gap-2 mt-4 text-sm text-soma-muted">
              <input type="checkbox" checked={form.published} onChange={(e) => setForm({ ...form, published: e.target.checked })} /> Published
            </label>
            <button type="submit" disabled={saving} data-testid="form-submit"
              className="mt-8 w-full flex items-center justify-center gap-2 py-3 rounded-full bg-soma-primary text-soma-bg font-semibold hover:bg-soma-primaryHover transition-colors disabled:opacity-60">
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : (editing ? "Save changes" : "Create program")}
            </button>
          </motion.form>
        </div>
      )}
    </div>
  );
}
